"use client";
import { LocaleLink } from "@i18n/routing";
import {
	type Config,
	config,
	isPlanSellable,
	isPricingV2Live,
} from "@repo/config";
import {
	AI_REPORT_CREDITS,
	AI_VISITS_PER_CREDIT,
	CITATION_REWRITE_CREDITS,
	FAQ_RUN_CREDITS,
	FREE_AI_VISITS_PER_MONTH,
	PROMPT_RUNS_PER_CREDIT,
	SITE_AUDIT_PAGES_PER_CREDIT,
	TRIAL_CREDITS,
	TRIAL_DAYS,
	getEntitlements,
} from "@repo/payments/client";
import { usePlanData } from "@saas/payments/hooks/plan-data";
import { usePlanCheckout } from "@saas/payments/hooks/use-plan-checkout";
import type { PlanId } from "@saas/payments/types";
import { useLocaleCurrency } from "@shared/hooks/locale-currency";
import { Button } from "@ui/components/button";
import { Tabs, TabsList, TabsTrigger } from "@ui/components/tabs";
import { cn } from "@ui/lib";
import {
	AlertCircle as AlertCircleIcon,
	ArrowRightIcon,
	BadgePercentIcon,
	CheckIcon,
	Coins as CoinsIcon,
	PhoneIcon,
	ShieldCheckIcon,
	StarIcon,
} from "lucide-react";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { PaygCreditsModal } from "./PaygCreditsModal";
import { PricingTableV2 } from "./PricingTableV2";

const plans = config.payments.plans as Config["payments"]["plans"];

export interface PricingTableProps {
	className?: string;
	userId?: string;
	propertyId?: string;
	organizationSlug?: string;
	organizationId?: string;
	activePlanId?: string;
	inlineMode?: boolean;
	onPlanSelected?: (planId: string, interval: string) => void;
}

/**
 * The plan picker, shared by /pricing and in-app billing. Renders the October
 * 2026 (v2) cards once `NEXT_PUBLIC_PRICING_V2=1`, and the v1 table before.
 */
export function PricingTable(props: PricingTableProps) {
	return isPricingV2Live() ? (
		<PricingTableV2 {...props} />
	) : (
		<PricingTableV1 {...props} />
	);
}

function PricingTableV1({
	className,
	userId,
	propertyId,
	organizationSlug,
	organizationId,
	activePlanId,
	inlineMode = false,
	onPlanSelected,
}: PricingTableProps) {
	const t = useTranslations();
	const localeCurrency = useLocaleCurrency();
	const [interval, setInterval] = useState<"month" | "quarter" | "year">(
		"month",
	);

	const { planData } = usePlanData();

	const {
		onSelectPlan: selectPlan,
		loading,
		isTrialUser,
		isExpiredTrial,
		paygModal,
	} = usePlanCheckout({
		userId,
		propertyId,
		organizationSlug,
		organizationId,
		inlineMode,
		onPlanSelected,
	});
	const onSelectPlan = (planId: PlanId, productId?: string) =>
		selectPlan(planId, interval, productId);

	// For trial/expired trial users, allow them to select their current plan (to upgrade from trial to paid)
	// For paying subscribers, exclude their current plan from selection
	const filteredPlans = Object.entries(plans).filter(
		([planId, plan]) =>
			// Trial and expired trial users can see their plan (to upgrade); paid users cannot
			(isTrialUser || isExpiredTrial || planId !== activePlanId) &&
			(!activePlanId || planId !== "free") &&
			!plan.hidden &&
			// Only plans on sale right now (v2 once the launch flag is on).
			isPlanSellable(planId) &&
			!plan.isEnterprise, // Exclude enterprise from main grid
	);

	const enterprisePlan = Object.entries(plans).find(
		([_, plan]) => plan.isEnterprise,
	);

	const hasSubscriptions = filteredPlans.some(([_, plan]) =>
		plan.prices?.some((price) => price.type === "recurring"),
	);

	return (
		<div className={cn("@container", className)}>
			{hasSubscriptions && (
				<div className="mb-6 flex @xl:justify-center">
					<Tabs
						value={interval}
						onValueChange={(value) =>
							setInterval(value as typeof interval)
						}
						data-test="price-table-interval-tabs"
					>
						<TabsList className="border-foreground/10">
							<TabsTrigger value="month">
								{t("pricing.monthly")}
							</TabsTrigger>
							<TabsTrigger value="quarter">
								{t("pricing.quarterly")}
							</TabsTrigger>
							<TabsTrigger value="year">
								{t("pricing.yearly")}
							</TabsTrigger>
						</TabsList>
					</Tabs>
				</div>
			)}

			{/* Your Current Plan Section */}
			{activePlanId &&
				plans[activePlanId as keyof typeof plans] &&
				!isTrialUser && (
					<div className="mb-6 rounded-2xl border-2 border-primary/50 bg-primary/5 p-4">
						<div className="flex items-center justify-between flex-wrap gap-4">
							<div className="flex items-center gap-3">
								<div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white">
									<CheckIcon className="h-4 w-4" />
								</div>
								<div>
									<div className="text-xs font-medium text-primary uppercase tracking-wide">
										{t("pricing.yourCurrentPlan")}
									</div>
									<div className="font-semibold text-lg">
										{planData[
											activePlanId as keyof typeof planData
										]?.title || activePlanId}
									</div>
								</div>
							</div>
							{activePlanId === "paygCredits" && (
								<div className="text-sm text-muted-foreground">
									{t("pricing.paygCreditsDescription")}
								</div>
							)}
						</div>
					</div>
				)}

			{/* Trial User Section - Show helpful upgrade prompt */}
			{isTrialUser &&
				activePlanId &&
				plans[activePlanId as keyof typeof plans] && (
					<div className="mb-6 rounded-2xl border-2 border-amber-500/50 bg-amber-50/50 dark:bg-amber-950/20 p-4">
						<div className="flex items-center gap-3">
							<div className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-500 text-white">
								<StarIcon className="h-4 w-4" />
							</div>
							<div>
								<div className="text-xs font-medium text-amber-600 dark:text-amber-400 uppercase tracking-wide">
									Free Trial
								</div>
								<div className="font-semibold text-lg">
									{planData[
										activePlanId as keyof typeof planData
									]?.title || activePlanId}
								</div>
								<p className="text-sm text-muted-foreground mt-1">
									Subscribe below to keep using all features
									when your trial ends
								</p>
							</div>
						</div>
					</div>
				)}

			{/* Expired Trial Section - Urgent upgrade prompt */}
			{isExpiredTrial &&
				activePlanId &&
				plans[activePlanId as keyof typeof plans] && (
					<div className="mb-6 rounded-2xl border-2 border-red-500/50 bg-red-50/50 dark:bg-red-950/20 p-4">
						<div className="flex items-center gap-3">
							<div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-500 text-white">
								<AlertCircleIcon className="h-4 w-4" />
							</div>
							<div>
								<div className="text-xs font-medium text-red-600 dark:text-red-400 uppercase tracking-wide">
									Trial Expired
								</div>
								<div className="font-semibold text-lg">
									{planData[
										activePlanId as keyof typeof planData
									]?.title || activePlanId}
								</div>
								<p className="text-sm text-muted-foreground mt-1">
									Your trial has ended. Subscribe now to
									continue using all features.
								</p>
							</div>
						</div>
					</div>
				)}

			<div
				className={cn("grid grid-cols-1 gap-4", {
					"@xl:grid-cols-2": filteredPlans.length >= 2,
					"@3xl:grid-cols-3": filteredPlans.length >= 3,
					"@4xl:grid-cols-4": filteredPlans.length >= 4,
				})}
			>
				{filteredPlans
					.filter(
						([planId]) =>
							isTrialUser ||
							isExpiredTrial ||
							planId !== activePlanId,
					)
					.map(([planId, plan]) => {
						const { isFree, isEnterprise, prices, credits } = plan;
						const {
							title,
							description,
							features,
							includedFeatures,
							creditFeatures,
						} = planData[planId as keyof typeof planData];

						// Find monthly price for baseline comparison
						const monthlyPrice = prices?.find(
							(p) =>
								!p.hidden &&
								p.type === "recurring" &&
								p.interval === "month" &&
								p.intervalCount === 1 &&
								p.currency === localeCurrency,
						);

						// Find price matching current interval selection
						let price = prices?.find(
							(price) =>
								!price.hidden &&
								(price.type === "one-time" ||
									(price.interval === "month" &&
										interval === "month" &&
										(price.intervalCount === 1 ||
											!price.intervalCount)) ||
									(price.interval === "month" &&
										interval === "quarter" &&
										price.intervalCount === 3) ||
									(price.interval === "year" &&
										interval === "year")) &&
								price.currency === localeCurrency,
						);

						if (isFree) {
							price = {
								amount: 0,
								currency: localeCurrency,
								interval: "month" as const,
								productId: "",
								type: "recurring",
							};
						}

						if (!(price || isEnterprise)) {
							return null;
						}

						// Calculate per-month price and savings
						let perMonthPrice = price?.amount || 0;
						let savingsPercent = 0;
						let totalPrice = price?.amount || 0;

						if (
							price &&
							"interval" in price &&
							price.type === "recurring"
						) {
							// For quarterly/annual, calculate per-month price
							if (
								interval === "quarter" &&
								price.intervalCount === 3
							) {
								perMonthPrice = Math.round(price.amount / 3);
								totalPrice = price.amount;
								if (monthlyPrice) {
									savingsPercent = Math.round(
										((monthlyPrice.amount - perMonthPrice) /
											monthlyPrice.amount) *
											100,
									);
								}
							} else if (interval === "year") {
								perMonthPrice = Math.round(price.amount / 12);
								totalPrice = price.amount;
								if (monthlyPrice) {
									savingsPercent = Math.round(
										((monthlyPrice.amount - perMonthPrice) /
											monthlyPrice.amount) *
											100,
									);
								}
							} else {
								// Monthly pricing
								perMonthPrice = price.amount;
								totalPrice = price.amount;
							}
						}

						// Plan-specific colors
						const planColors = {
							paygCredits: "emerald",
							brand: "amber",
							agencyCore: "blue",
							agencyCoreUsageBased: "blue",
						};
						const planColor =
							planColors[planId as keyof typeof planColors] ||
							"primary";

						return (
							<div
								key={planId}
								className="rounded-3xl border-2 p-6"
								style={{
									borderColor:
										planColor === "emerald"
											? "rgb(16 185 129)"
											: planColor === "amber"
												? "rgb(245 158 11)"
												: planColor === "blue"
													? "rgb(59 130 246)"
													: undefined,
								}}
								data-test="price-table-plan"
							>
								<div className="flex h-full flex-col justify-between gap-4">
									<div>
										{savingsPercent > 0 && (
											<div className="-mt-9 flex justify-center">
												<div
													className="mb-2 flex h-6 w-auto items-center gap-1.5 rounded-full px-2 py-1 font-semibold text-white text-xs"
													style={{
														backgroundColor:
															planColor ===
															"emerald"
																? "rgb(16 185 129)"
																: planColor ===
																		"amber"
																	? "rgb(245 158 11)"
																	: planColor ===
																			"blue"
																		? "rgb(59 130 246)"
																		: undefined,
													}}
												>
													<BadgePercentIcon className="size-3" />
													Save {savingsPercent}%
												</div>
											</div>
										)}
										<h3
											className="my-0 font-semibold text-2xl"
											style={{
												color:
													planColor === "emerald"
														? "rgb(16 185 129)"
														: planColor === "amber"
															? "rgb(245 158 11)"
															: planColor ===
																	"blue"
																? "rgb(59 130 246)"
																: undefined,
											}}
										>
											{title}
										</h3>
										{credits &&
											(() => {
												const includedPrompts =
													getEntitlements(
														planId,
													).trackedPrompts;
												const chipStyle = {
													backgroundColor:
														planColor === "emerald"
															? "rgb(16 185 129 / 0.1)"
															: planColor ===
																	"amber"
																? "rgb(245 158 11 / 0.1)"
																: planColor ===
																		"blue"
																	? "rgb(59 130 246 / 0.1)"
																	: undefined,
													color:
														planColor === "emerald"
															? "rgb(16 185 129)"
															: planColor ===
																	"amber"
																? "rgb(245 158 11)"
																: planColor ===
																		"blue"
																	? "rgb(59 130 246)"
																	: undefined,
												};
												return (
													<div
														className="mt-2 rounded-lg px-4 py-3 font-semibold text-sm"
														style={chipStyle}
													>
														<div className="mb-1.5 text-center text-xs uppercase tracking-wide opacity-80">
															{t(
																"pricing.chips.includes",
															)}
														</div>
														<div
															className={cn(
																"grid gap-4",
																includedPrompts
																	? "grid-cols-2"
																	: "grid-cols-1",
															)}
														>
															<div className="flex flex-col items-center gap-0.5 text-center">
																<span className="text-2xl leading-none">
																	{credits}
																</span>
																<span className="text-xs font-medium">
																	{t(
																		"pricing.chips.creditsPerMonth",
																	)}
																</span>
															</div>
															{includedPrompts ? (
																<div className="flex flex-col items-center gap-0.5 text-center">
																	<span className="text-2xl leading-none">
																		{
																			includedPrompts
																		}
																	</span>
																	<span className="text-xs font-medium">
																		{t(
																			"pricing.chips.promptsPerMonth",
																		)}
																	</span>
																</div>
															) : null}
														</div>
													</div>
												);
											})()}
										{description && (
											<div className="prose mt-2 text-foreground/60 text-sm">
												{description}
											</div>
										)}

										{includedFeatures?.length &&
										creditFeatures?.length ? (
											<div className="mt-4 space-y-4">
												<div>
													<div className="mb-2 font-semibold text-foreground/50 text-xs uppercase tracking-wide">
														{t(
															"pricing.planGroups.includedMonthly",
														)}
													</div>
													<ul className="grid list-none gap-2 text-sm">
														{includedFeatures.map(
															(feature, key) => (
																<li
																	key={key}
																	className="flex items-start justify-start"
																>
																	<CheckIcon className="mr-2 mt-0.5 size-4 shrink-0 text-primary" />
																	<span>
																		{
																			feature
																		}
																	</span>
																</li>
															),
														)}
													</ul>
												</div>
												<div>
													<div className="mb-2 font-semibold text-foreground/50 text-xs uppercase tracking-wide">
														{t(
															"pricing.planGroups.creditBundle",
															{
																credits:
																	credits ??
																	0,
															},
														)}
													</div>
													<ul className="grid list-none gap-2 text-sm">
														{creditFeatures.map(
															(feature, key) => (
																<li
																	key={key}
																	className="flex items-start justify-start"
																>
																	<CoinsIcon className="mr-2 mt-0.5 size-4 shrink-0 text-primary" />
																	<span>
																		{
																			feature
																		}
																	</span>
																</li>
															),
														)}
													</ul>
												</div>
											</div>
										) : (
											!!features?.length && (
												<ul className="mt-4 grid list-none gap-2 text-sm">
													{features.map(
														(feature, key) => (
															<li
																key={key}
																className="flex items-center justify-start"
															>
																<CheckIcon className="mr-2 size-4 text-primary" />
																<span>
																	{feature}
																</span>
															</li>
														),
													)}
												</ul>
											)
										)}

										{price &&
											"trialPeriodDays" in price &&
											price.trialPeriodDays && (
												<div className="mt-4 flex items-center justify-start font-medium text-primary text-sm opacity-80">
													<BadgePercentIcon className="mr-2 size-4" />
													{t("pricing.trialPeriod", {
														days: price.trialPeriodDays,
													})}
												</div>
											)}
									</div>

									<div>
										{price &&
											(price.type === "one-time" ? (
												// PAYG: Show per-credit pricing
												<strong
													className="block font-medium text-2xl lg:text-3xl"
													data-test="price-table-plan-price"
												>
													{Intl.NumberFormat(
														"en-US",
														{
															style: "currency",
															currency:
																price.currency,
															minimumFractionDigits: 0,
														},
													).format(price.amount)}
													<span className="font-normal text-xs opacity-60">
														{" per credit"}
													</span>
												</strong>
											) : (
												// Subscription plans: Show per-month pricing
												<>
													<strong
														className="block font-medium text-2xl lg:text-3xl"
														data-test="price-table-plan-price"
													>
														{Intl.NumberFormat(
															"en-US",
															{
																style: "currency",
																currency:
																	price.currency,
																minimumFractionDigits: 0,
															},
														).format(perMonthPrice)}
														<span className="font-normal text-xs opacity-60">
															{" / month"}
														</span>
														{propertyId &&
															"seatBased" in
																price &&
															price.seatBased && (
																<span className="font-normal text-xs opacity-60">
																	{" / "}
																	{t(
																		"pricing.perSeat",
																	)}
																</span>
															)}
													</strong>
													{interval !== "month" &&
														"interval" in price && (
															<div className="mt-1 text-foreground/60 text-xs">
																{interval ===
																	"quarter" &&
																	`Billed quarterly at ${Intl.NumberFormat(
																		"en-US",
																		{
																			style: "currency",
																			currency:
																				price.currency,
																			minimumFractionDigits: 0,
																		},
																	).format(
																		totalPrice,
																	)}`}
																{interval ===
																	"year" &&
																	`Billed annually at ${Intl.NumberFormat(
																		"en-US",
																		{
																			style: "currency",
																			currency:
																				price.currency,
																			minimumFractionDigits: 0,
																		},
																	).format(
																		totalPrice,
																	)}`}
															</div>
														)}
												</>
											))}

										{isEnterprise ? (
											<Button
												className="mt-4 w-full"
												variant="light"
												asChild
											>
												<LocaleLink href="/contact">
													<PhoneIcon className="mr-2 size-4" />
													{t("pricing.contactSales")}
												</LocaleLink>
											</Button>
										) : (
											<button
												type="button"
												className="mt-4 w-full rounded-lg px-4 py-2 font-semibold text-white transition-all hover:opacity-90 disabled:opacity-50"
												style={{
													backgroundColor:
														planColor === "emerald"
															? "rgba(16, 185, 129, 0.7)"
															: planColor ===
																	"amber"
																? "rgba(245, 158, 11, 0.85)"
																: planColor ===
																		"blue"
																	? "rgba(59, 130, 246, 0.9)"
																	: "rgba(59, 130, 246, 0.8)",
												}}
												onClick={() =>
													onSelectPlan(
														planId as PlanId,
														price?.productId,
													)
												}
												disabled={loading === planId}
											>
												<span className="flex items-center justify-center">
													{loading === planId ? (
														<span className="mr-2">
															Loading...
														</span>
													) : (
														<>
															{inlineMode
																? "Select this plan"
																: (isTrialUser ||
																			isExpiredTrial) &&
																		planId ===
																			activePlanId
																	? "Subscribe Now"
																	: userId
																		? t(
																				"pricing.choosePlan",
																			)
																		: t(
																				"pricing.getStarted",
																			)}
															<ArrowRightIcon className="ml-2 size-4" />
														</>
													)}
												</span>
											</button>
										)}
									</div>
								</div>
							</div>
						);
					})}
			</div>

			{/* Overage trust line — the consent flow guarantees this promise */}
			{!inlineMode && (
				<div className="mt-6 flex items-center justify-center gap-2 text-center text-muted-foreground text-sm">
					<ShieldCheckIcon className="size-4 shrink-0 text-primary" />
					<span>{t("pricing.noSurpriseCharges")}</span>
				</div>
			)}

			{/* Enterprise Section - Hide in inline mode */}
			{!inlineMode && enterprisePlan && (
				<div className="mt-12">
					<div className="rounded-3xl border-2 bg-gradient-to-br from-muted/50 to-muted/30 p-8">
						{(() => {
							const [planId, plan] = enterprisePlan;
							const { title, description, features } =
								planData[planId as keyof typeof planData];

							return (
								<div className="flex flex-col items-center gap-6 @xl:flex-row @xl:items-start @xl:justify-between">
									<div className="flex-1 text-center @xl:text-left">
										<h3 className="mb-2 font-bold text-3xl">
											{title}
										</h3>
										<div className="prose mb-6 text-foreground/70">
											{description}
										</div>
										{!!features?.length && (
											<ul className="inline-grid gap-3 text-left text-sm @xl:grid-cols-2">
												{features.map(
													(feature, key) => (
														<li
															key={key}
															className="flex items-start"
														>
															<CheckIcon className="mr-2 mt-0.5 size-4 shrink-0 text-primary" />
															<span>
																{feature}
															</span>
														</li>
													),
												)}
											</ul>
										)}
									</div>
									<div className="flex shrink-0 flex-col items-center gap-4">
										<div className="text-center">
											<div className="mb-1 font-semibold text-foreground/60 text-sm">
												Custom Pricing
											</div>
											<div className="font-bold text-2xl">
												Contact Sales
											</div>
										</div>
										<Button
											className="w-full min-w-[200px]"
											variant="secondary"
											size="lg"
											asChild
										>
											<LocaleLink href="/contact">
												<PhoneIcon className="mr-2 size-4" />
												{t("pricing.contactSales")}
											</LocaleLink>
										</Button>
									</div>
								</div>
							);
						})()}
					</div>
				</div>
			)}

			{/* Common Features & Credit Costs - below the plan tiers, hide in inline mode */}
			{!inlineMode && (
				<div
					id="all-plans-include"
					className="mt-12 grid scroll-mt-24 grid-cols-1 gap-8 @xl:grid-cols-2"
				>
					{/* Common Features */}
					<div className="rounded-2xl border bg-muted/30 p-6">
						<h3 className="mb-4 font-semibold text-lg">
							{t("pricing.commonFeatures.title")}
						</h3>
						<ul className="grid gap-3 text-sm">
							<li className="flex items-start">
								<CheckIcon className="mr-2 mt-0.5 size-4 shrink-0 text-primary" />
								<span>
									{t("pricing.commonFeatures.botTracking")}
								</span>
							</li>
							<li className="flex items-start">
								<CheckIcon className="mr-2 mt-0.5 size-4 shrink-0 text-primary" />
								<span>
									{t.rich(
										"pricing.commonFeatures.publisherScoring",
										{
											aipvs: (chunks) => (
												<LocaleLink
													href="/ai-placement-value-score"
													className="font-medium text-primary underline underline-offset-2"
												>
													{chunks}
												</LocaleLink>
											),
											aipqs: (chunks) => (
												<LocaleLink
													href="/ai-placement-quality-score"
													className="font-medium text-primary underline underline-offset-2"
												>
													{chunks}
												</LocaleLink>
											),
										},
									)}
								</span>
							</li>
							<li className="flex items-start">
								<CheckIcon className="mr-2 mt-0.5 size-4 shrink-0 text-primary" />
								<span>
									{t.rich(
										"pricing.commonFeatures.citationScoring",
										{
											link: (chunks) => (
												<LocaleLink
													href="/citation-optimizer"
													className="font-medium text-primary underline underline-offset-2"
												>
													{chunks}
												</LocaleLink>
											),
										},
									)}
								</span>
							</li>
							<li className="flex items-start">
								<CheckIcon className="mr-2 mt-0.5 size-4 shrink-0 text-primary" />
								<span>
									{t(
										"pricing.commonFeatures.competitorSites",
									)}
								</span>
							</li>
							<li className="flex items-start">
								<CheckIcon className="mr-2 mt-0.5 size-4 shrink-0 text-primary" />
								<span>
									{t("pricing.commonFeatures.unlimitedUsers")}
								</span>
							</li>
							<li className="flex items-start">
								<CheckIcon className="mr-2 mt-0.5 size-4 shrink-0 text-primary" />
								<span>
									{t("pricing.commonFeatures.customPrompts")}
								</span>
							</li>
						</ul>
					</div>

					{/* Credit Costs */}
					<div className="rounded-2xl border bg-muted/30 p-6">
						<h3 className="mb-4 font-semibold text-lg">
							{t("pricing.creditCosts.title")}
						</h3>
						<ul className="grid gap-3 text-sm">
							<li className="flex items-start">
								<span className="mr-2 font-semibold text-primary">
									{TRIAL_CREDITS}
								</span>
								<span>
									{t("pricing.creditCosts.trialRest", {
										days: TRIAL_DAYS,
									})}
								</span>
							</li>
							<li className="flex items-start">
								<span className="mr-2 font-semibold text-primary">
									{AI_REPORT_CREDITS}
								</span>
								<span>
									{t("pricing.creditCosts.aiReportRest")}
								</span>
							</li>
							<li className="flex items-start">
								<span className="mr-2 font-semibold text-primary">
									1
								</span>
								<span>
									{t(
										"pricing.creditCosts.promptTrackingRest",
										{ runs: PROMPT_RUNS_PER_CREDIT },
									)}
								</span>
							</li>
							<li className="flex items-start">
								<span className="mr-2 font-semibold text-primary">
									{CITATION_REWRITE_CREDITS}
								</span>
								<span>
									{t.rich(
										"pricing.creditCosts.citationRewriteRest",
										{
											link: (chunks) => (
												<LocaleLink
													href="/citation-optimizer"
													className="font-medium text-primary underline underline-offset-2"
												>
													{chunks}
												</LocaleLink>
											),
										},
									)}
								</span>
							</li>
							<li className="flex items-start">
								<span className="mr-2 font-semibold text-primary">
									1
								</span>
								<span>
									{t("pricing.creditCosts.siteAuditRest", {
										pages: SITE_AUDIT_PAGES_PER_CREDIT,
									})}
								</span>
							</li>
							<li className="flex items-start">
								<span className="mr-2 font-semibold text-primary">
									{FAQ_RUN_CREDITS}
								</span>
								<span>{t("pricing.creditCosts.faqRest")}</span>
							</li>
							<li className="flex items-start">
								<span className="mr-2 font-semibold text-primary">
									1
								</span>
								<span>
									{t("pricing.creditCosts.aiVisitsRest", {
										visits: AI_VISITS_PER_CREDIT,
										free: FREE_AI_VISITS_PER_MONTH.toLocaleString(),
									})}
								</span>
							</li>
						</ul>
					</div>
				</div>
			)}

			{/* PAYG Credits Selection Modal */}
			<PaygCreditsModal {...paygModal} />
		</div>
	);
}
